import { useMemo, useState, useEffect, useRef, useCallback } from "react"

import { Box, Button, Stack, Typography, useMediaQuery } from "@mui/material"
import { SkipassSolid } from "@skistarab/icons"
import theme from "@skistarab/mui-theme"
import { useTranslation } from "react-i18next"

import {
  OrderSummaryCard,
  PageContainer,
  StyledFormList,
  StyledHelperText,
} from "@/components"
import { CheckoutSection } from "@/components/CheckoutSection"
import { ConfirmNavigationDialog } from "@/components/ConfirmNavigationDialog"
import StatusAccordion, {
  StatusAccordionRef,
} from "@/components/StatusAccordion/StatusAccordion"
import { useDataLayer } from "@/features/DataLayer"
import PassHolderForm from "@/features/PassHolderForm"
import type { PassHolderFormRef } from "@/features/PassHolderForm"
import { useIsSummer, useSelectedBookingLength } from "@/hooks"
import useProductTotals from "@/hooks/useProductTotals"
import { useDateParam, useResortParam } from "@/hooks/useSkipassFlow"
import { useSkipassStore } from "@/store/skipassStore"
import { PassHolderFormState } from "@/types/PassHolder"

import { createInitialPassHolders } from "./helpers/createInitialPassHolders"
import { useCartActions } from "./hooks/useCartActions"
import useOrderTotal from "./hooks/useOrderTotal"
import { useConfirmBeforeAction } from "../SelectPassPage/hooks/useConfirmBeforeAction"

type PassHoldersDetailsPageProps = {
  onBackNavigation: () => void
}
const PassHoldersDetailsPage = ({
  onBackNavigation,
}: PassHoldersDetailsPageProps) => {
  const { t } = useTranslation()
  const isSummer = useIsSummer()
  const { selectedProducts, totalPriceText } = useProductTotals()
  const decrementProduct = useSkipassStore((state) => state.decrementProduct)
  const { selectedBookingLength } = useSelectedBookingLength()
  const { startDate } = useDateParam()
  const { resortExternalId } = useResortParam()
  const saveSkipassSkiersDetails = useDataLayer("save_skipass_skiers_details")

  const isTablet = useMediaQuery(theme.breakpoints.down("md"))
  const accordionRefs = useRef<Map<string, StatusAccordionRef>>(new Map())
  const formRefs = useRef<Map<string, PassHolderFormRef>>(new Map())

  const { isLoading, error, addSkipassItemsToCart } = useCartActions()
  const [openAccordionId, setOpenAccordionId] = useState<string | null>(null)
  const [validationError, setValidationError] = useState<string>()
  const [showIndividualValidation, setShowIndividualValidation] =
    useState(false)

  const initialPassHolders = useMemo(
    () => createInitialPassHolders(selectedProducts),
    [selectedProducts]
  )

  const [passHolders, setPassHolders] =
    useState<PassHolderFormState[]>(initialPassHolders)

  const usedWtpNumbers = passHolders.map((ph) => ph.wtpNumber)

  const allFormsCompleted = passHolders.every((ph) => ph.isFormSubmitted)
  const hasAnyFormCompleted = passHolders.some((ph) => ph.isFormSubmitted)

  const handleFormSave = (updatedPassHolder: PassHolderFormState) => {
    const savedId = updatedPassHolder.passHolder.id

    saveSkipassSkiersDetails({
      Category: selectedBookingLength?.name ?? "",
      MinLength: selectedBookingLength?.minLength?.toString() ?? "",
    })

    setPassHolders((prev) =>
      prev.map((ph) =>
        ph.passHolder.id === savedId
          ? { ...updatedPassHolder, isFormSubmitted: true }
          : ph
      )
    )

    setOpenAccordionId(null)
    const scrollTargetId = isTablet
      ? "pass-holder-form-list"
      : "page-container-stack"
    document.getElementById(scrollTargetId)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    })

    const accordionRef = accordionRefs.current.get(savedId)

    setTimeout(() => {
      accordionRef?.focus()
    }, 200)
  }

  const handleRemovePassHolder = (
    passHolderId: string,
    productPriceId: string
  ) => {
    decrementProduct(productPriceId)

    setPassHolders((prev) =>
      prev.filter((ph) => ph.passHolder.id !== passHolderId)
    )

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }

    setTimeout(() => {
      document.getElementById("pass-holder-form-list")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      })
    }, 200)
  }

  const handleToggleAccordion = (passHolderId: string) => {
    setOpenAccordionId((prev) => (prev === passHolderId ? null : passHolderId))
  }

  const onAddToCart = () => {
    if (!allFormsCompleted) {
      setShowIndividualValidation(true)
      setValidationError(
        t("passHolderDetails.form.errors.missingInformation", {
          context: isSummer ? "summer" : undefined,
        })
      )

      const firstIncomplete = passHolders.find((ph) => !ph.isFormSubmitted)
      if (firstIncomplete) {
        const passHolderId = firstIncomplete.passHolder.id
        setOpenAccordionId(passHolderId)

        setTimeout(async () => {
          const formRef = formRefs.current.get(passHolderId)
          await formRef?.validateAndScrollToError()
        }, 400)
      }
      return
    }
    addSkipassItemsToCart(passHolders)
  }

  useEffect(() => {
    if (allFormsCompleted) {
      setValidationError(undefined)
      setShowIndividualValidation(false)
    }
  }, [allFormsCompleted, validationError])

  const { showDialog, checkAndProceed, handleConfirm, handleCancel } =
    useConfirmBeforeAction(hasAnyFormCompleted)

  const handleBackNavigation = () => {
    checkAndProceed(() => onBackNavigation())
  }

  const { orderTotalPriceText } = useOrderTotal(passHolders)

  const getPassHolderDisplayData = useCallback(
    (ph: PassHolderFormState) => ({
      id: ph.passHolder.id,
      title: `${ph.productName} - ${ph.ageGroup}`,
      subtitle:
        ph.passHolder.firstName && ph.passHolder.lastName
          ? `${ph.passHolder.firstName} ${ph.passHolder.lastName}`
          : t("passHolderDetails.form.fillInSkierDetails", {
              context: isSummer ? "summer" : undefined,
            }),
      isCompleted: ph.isFormSubmitted,
      productPriceId: ph.productPriceId,
      formId: `passHolderForm-${ph.passHolder.id}`,
    }),
    [isSummer, t]
  )

  return (
    <>
      <PageContainer
        headline={t("passHolderDetails.title", {
          context: isSummer ? "summer" : undefined,
        })}
        onBackClick={handleBackNavigation}
        activeStep={1}
        leftIcon={<SkipassSolid />}
        isSummer={isSummer}
      >
        <ConfirmNavigationDialog
          open={showDialog}
          onClose={handleCancel}
          onSecondaryAction={handleCancel}
          onPrimaryAction={() => handleConfirm()}
          headingText={t("passHolderDetails.confirmNavigationDialog.heading", {
            context: isSummer ? "summer" : undefined,
          })}
          bodyText={t("passHolderDetails.confirmNavigationDialog.body", {
            context: isSummer ? "summer" : undefined,
          })}
          secondaryButtonText={t(
            "passHolderDetails.confirmNavigationDialog.cancelButtonLabel"
          )}
          primaryButtonText={t(
            "passHolderDetails.confirmNavigationDialog.clearButtonLabel"
          )}
        />

        <Stack
          spacing={{ xs: 2, lg: 4, xxl: 8 }}
          direction={{ xs: "column", md: "row" }}
        >
          {isTablet && (
            <Box sx={{ width: "100%" }}>
              <OrderSummaryCard
                bookingLengthName={selectedBookingLength?.name}
              />
            </Box>
          )}

          <StyledFormList id="pass-holder-form-list">
            <Typography variant="h6" component="h2" fontWeight="bold">
              {t("passHolderDetails.yourPasses")}
            </Typography>

            {passHolders.map((ph) => {
              const displayData = getPassHolderDisplayData(ph)

              return (
                <StatusAccordion
                  key={displayData.id}
                  ref={(el) => {
                    if (el) {
                      accordionRefs.current.set(displayData.id, el)
                    } else {
                      accordionRefs.current.delete(displayData.id)
                    }
                  }}
                  open={openAccordionId === displayData.id}
                  onToggle={() => handleToggleAccordion(displayData.id)}
                  hasError={
                    showIndividualValidation && !displayData.isCompleted
                  }
                  {...displayData}
                >
                  <PassHolderForm
                    ref={(el) => {
                      if (el) {
                        formRefs.current.set(displayData.id, el)
                      } else {
                        formRefs.current.delete(displayData.id)
                      }
                    }}
                    passHolder={ph}
                    onSave={handleFormSave}
                    usedWtpNumbers={usedWtpNumbers}
                    formId={displayData.formId}
                    startDate={startDate}
                    resortExternalId={resortExternalId}
                  />
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mt: 2 }}
                  >
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() =>
                        handleRemovePassHolder(
                          displayData.id,
                          displayData.productPriceId
                        )
                      }
                    >
                      {t("passHolderDetails.form.removeButtonLabel", {
                        context: isSummer ? "summer" : undefined,
                      })}
                    </Button>
                    <Button
                      type="submit"
                      form={displayData.formId}
                      variant="outlined"
                    >
                      {t("passHolderDetails.form.saveButtonLabel", {
                        context: isSummer ? "summer" : undefined,
                      })}
                    </Button>
                  </Stack>
                </StatusAccordion>
              )
            })}
            {validationError && (
              <StyledHelperText helperText={validationError} severity="error" />
            )}
          </StyledFormList>

          <Stack
            direction="column"
            spacing={2}
            sx={{
              width: { xs: "100%", lg: 360 },
              flexShrink: 0,
            }}
            flex={1}
            alignSelf="flex-start"
          >
            {!isTablet && (
              <OrderSummaryCard
                bookingLengthName={selectedBookingLength?.name}
              />
            )}

            <CheckoutSection
              orderTotalPriceText={orderTotalPriceText}
              isLoading={isLoading}
              error={error}
              onCheckout={onAddToCart}
              monthlyPriceText={
                passHolders[0]?.isSubscriptionProduct
                  ? totalPriceText
                  : undefined
              }
            />
          </Stack>
        </Stack>
      </PageContainer>
    </>
  )
}

export default PassHoldersDetailsPage
